import { Button, Input } from '@atlas/ui-primitives'
import type { PromptVariable } from '@/domain/prompt.types'

/**
 * What a `join` composes, and what it renders as.
 *
 * A join was previously invisible in the editor: `joins` could not be set at all, so
 * a body of `{{direction}}` referred to a variable whose parts existed only in seed
 * SQL. An author could not tell what `direction` was, which parts fed it, or why the
 * fields they could see never appeared in the body — the composition IS the
 * explanation, so it is shown rather than described.
 *
 * Empty parts are skipped at render, which is the reason the type exists: a body of
 * `{{a}}, {{b}}, {{c}}` renders as ", , " when only `b` is set.
 */
export function JoinControl({
	variable,
	labelPrefix,
	siblings,
	isDisabled,
	onPatch,
}: {
	variable: PromptVariable
	labelPrefix: string
	siblings: PromptVariable[]
	isDisabled: boolean
	onPatch: (next: Partial<PromptVariable>) => void
}) {
	const parts = variable.joins ?? []
	const separator = variable.separator ?? ', '

	const toggle = (key: string) => {
		onPatch({ joins: parts.includes(key) ? parts.filter((k) => k !== key) : [...parts, key] })
	}

	return (
		<div className="space-y-2">
			<div>
				<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
					Composed from — click to add, in render order
				</span>
				{siblings.length === 0 ? (
					<p className="text-ds-2xs text-ds-text-muted">Add other variables first; a join composes them.</p>
				) : (
					<ul className="flex flex-wrap gap-1.5">
						{siblings.map((sibling) => (
							<li key={sibling.key}>
								<Button
									size="sm"
									variant={parts.includes(sibling.key) ? 'secondary' : 'muted'}
									isDisabled={isDisabled}
									aria-pressed={parts.includes(sibling.key)}
									onPress={() => toggle(sibling.key)}
								>
									{sibling.key}
								</Button>
							</li>
						))}
					</ul>
				)}
			</div>

			{/*
				The composition spelled out, because the order matters and is not visible
				from a set of toggles alone.
			*/}
			{parts.length > 0 ? (
				<p className="rounded-ds-md border border-ds-border bg-ds-surface p-2 font-mono text-ds-2xs text-ds-text">
					{'{{'}
					{variable.key || 'this'}
					{'}}'} = {parts.map((k) => `{{${k}}}`).join(separator)}
				</p>
			) : (
				<p className="text-ds-2xs text-ds-warning">
					Composes nothing yet — this renders as empty text wherever the body names it.
				</p>
			)}

			<label className="block">
				<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Separator</span>
				<Input
					value={separator}
					aria-label={`${labelPrefix} separator`}
					className="font-mono"
					disabled={isDisabled}
					onChange={(e) => onPatch({ separator: e.target.value })}
				/>
			</label>
		</div>
	)
}
